import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useSiteContent } from "@/siteContent";

const USER = "Duru";

const isVideo = (url) => typeof url === "string" && /\.(mp4|webm|mov)(\?|$)/i.test(url);

const SECTIONS = [
  { id: "hero", label: "Anasayfa" },
  { id: "manifesto", label: "Manifesto" },
  { id: "works", label: "İşler" },
  { id: "projects", label: "Projeler" },
  { id: "skills", label: "Zanaat (Yetenekler)" },
  { id: "langedu", label: "Diller & Eğitim" },
  { id: "contact", label: "İletişim" },
  { id: "footer", label: "Footer" },
  { id: "media", label: "Medya (foto/video)" },
];

const LIST_KEYS = {
  manifesto: "manifesto.list",
  works: "works.list",
  projects: "projects.list",
  skills: "skills.groups",
  languages: "languages.list",
  education: "education.list",
  colophon: "footer.colophon",
};

const INPUT =
  "mt-1 w-full bg-[color:var(--paper)] border-2 border-[color:var(--ink)] rounded-md p-2 text-sm outline-none";

/* ——— module-scope helpers (stable identity → inputs keep focus) ——— */

function Field({ defaultValue, label, textarea, onSave, testid }) {
  return (
    <label className="block">
      <span className="font-mono-label text-[10px] uppercase tracking-widest opacity-70">
        {label}
      </span>
      {textarea ? (
        <textarea
          defaultValue={defaultValue}
          onBlur={(e) => onSave(e.target.value)}
          rows={3}
          className={`${INPUT} resize-none`}
          data-testid={testid}
        />
      ) : (
        <input
          defaultValue={defaultValue}
          onBlur={(e) => onSave(e.target.value)}
          className={INPUT}
          data-testid={testid}
        />
      )}
    </label>
  );
}

function TextInput({ value, onChange, placeholder, textarea, testid }) {
  return textarea ? (
    <textarea
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      rows={3}
      placeholder={placeholder}
      className={`${INPUT} resize-none`}
      data-testid={testid}
    />
  ) : (
    <input
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={INPUT}
      data-testid={testid}
    />
  );
}

function LinesInput({ value, onChange, label, testid }) {
  const arr = Array.isArray(value) ? value : [];
  return (
    <label className="block">
      <span className="font-mono-label text-[10px] uppercase tracking-widest opacity-60">
        {label} (her satır bir madde)
      </span>
      <textarea
        value={arr.join("\n")}
        onChange={(e) => onChange(e.target.value.split("\n"))}
        rows={4}
        className={`${INPUT} resize-none`}
        data-testid={testid}
      />
    </label>
  );
}

function ColorInput({ value, onChange }) {
  return (
    <label className="block">
      <span className="font-mono-label text-[10px] uppercase tracking-widest opacity-60">
        Renk kodu (örn: #ff5d8f)
      </span>
      <div className="mt-1 flex items-center gap-2">
        <input
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="#ff5d8f"
          className="flex-1 bg-[color:var(--paper)] border-2 border-[color:var(--ink)] rounded-md p-2 text-sm outline-none"
        />
        <span
          className="w-8 h-8 rounded-md border-2 border-[color:var(--ink)] shrink-0"
          style={{ background: value || "transparent" }}
          title="önizleme"
        />
      </div>
    </label>
  );
}

function RemoveBtn({ onClick, testid }) {
  return (
    <button
      onClick={onClick}
      className="font-mono-label text-[10px] uppercase tracking-widest border-2 border-[color:var(--ink)] rounded-full px-3 py-1 bg-[color:var(--persimmon)] text-[color:var(--paper)]"
      data-testid={testid}
    >
      Sil
    </button>
  );
}

function AddBtn({ onClick, label, testid }) {
  return (
    <button onClick={onClick} className="btn-pill sun" data-testid={testid}>
      + {label}
    </button>
  );
}

function Card({ children, testid }) {
  return (
    <div
      className="rounded-md border-2 border-[color:var(--ink)] bg-[color:var(--paper)] p-4 space-y-2"
      data-testid={testid}
    >
      {children}
    </div>
  );
}

export default function AdminPanel() {
  const site = useSiteContent();
  const [open, setOpen] = useState(false);
  const [loggedIn, setLoggedIn] = useState(
    () => !!localStorage.getItem("duru_admin_token")
  );
  const [creds, setCreds] = useState({ user: "", pass: "" });
  const [err, setErr] = useState("");
  const [lists, setLists] = useState({});
  const [savedFlash, setSavedFlash] = useState(false);
  const [activeSection, setActiveSection] = useState("hero");

  useEffect(() => {
    let buffer = "";
    let lastTs = 0;
    const CODE = "duru123";
    const onKey = (e) => {
      if (e.code === "Escape" && open) {
        setOpen(false);
        return;
      }
      const isFormEl =
        document.activeElement &&
        ["INPUT", "TEXTAREA"].includes(document.activeElement.tagName);
      if (isFormEl) return;
      if (e.key && e.key.length === 1) {
        const now = Date.now();
        if (now - lastTs > 1400) buffer = "";
        lastTs = now;
        buffer = (buffer + e.key.toLowerCase()).slice(-CODE.length);
        if (buffer === CODE && window.scrollY < 30) {
          e.preventDefault();
          setOpen(true);
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const hydrate = () => {
    const next = {};
    Object.entries(LIST_KEYS).forEach(([name, key]) => {
      next[name] = site.getList(key);
    });
    setLists(next);
  };

  useEffect(() => {
    if (loggedIn) hydrate();
     
  }, [loggedIn]);

  const login = async (e) => {
    e.preventDefault();
    if (creds.user !== USER) {
      setErr("Yanlış kullanıcı adı veya şifre.");
      return;
    }
    try {
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      const res = await fetch(`${API}/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: creds.pass }),
      });
      if (!res.ok) {
        setErr("Yanlış kullanıcı adı veya şifre.");
        return;
      }
      const data = await res.json();
      if (data && data.token) {
        localStorage.setItem("duru_admin_token", data.token);
        setLoggedIn(true);
        setErr("");
      } else {
        setErr("Sunucu hatası. Tekrar dene.");
      }
    } catch (e) {
      setErr("Bağlantı hatası. Tekrar dene.");
    }
  };

  const flashSaved = () => {
    setSavedFlash(true);
    setTimeout(() => setSavedFlash(false), 1200);
  };

  const saveField = (key, val) => {
    site.set(key, val);
    flashSaved();
  };

  const saveList = (name, arr) => {
    setLists((p) => ({ ...p, [name]: arr }));
    site.setList(LIST_KEYS[name], arr);
    flashSaved();
  };

  const patchItem = (name, i, patch) =>
    saveList(
      name,
      (lists[name] || []).map((it, idx) =>
        idx === i ? (typeof it === "string" ? patch : { ...it, ...patch }) : it,
      ),
    );
  const addItem = (name, blank) => saveList(name, [...(lists[name] || []), blank]);
  const removeItem = (name, i) =>
    saveList(name, (lists[name] || []).filter((_, idx) => idx !== i));

  const uploadTo = (key) => (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => saveField(key, reader.result);
    reader.readAsDataURL(f);
  };
  const uploadProjectPhoto = (i) => (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => patchItem("projects", i, { photo: reader.result });
    reader.readAsDataURL(f);
  };

  const renderMediaCard = (key, label) => {
    const current = site.get(key);
    return (
      <div
        className="rounded-md border-2 border-[color:var(--ink)] p-3 bg-[color:var(--paper)]"
        data-testid={`admin-media-${key}`}
      >
        <div className="font-mono-label text-[10px] uppercase tracking-widest opacity-70">
          {label}
        </div>
        {current ? (
          isVideo(current) ? (
            <video src={current} muted loop autoPlay playsInline className="mt-2 w-full h-40 object-cover rounded border border-[color:var(--ink)]" />
          ) : (
            <img src={current} alt="" className="mt-2 w-full h-40 object-cover rounded border border-[color:var(--ink)]" />
          )
        ) : (
          <div className="mt-2 w-full h-40 flex items-center justify-center rounded border border-dashed border-[color:var(--ink)] opacity-50 text-xs">
            Boş
          </div>
        )}
        <div className="mt-2 flex gap-2 flex-wrap items-center">
          <input type="file" accept="image/*,video/*" onChange={uploadTo(key)} className="text-xs" data-testid={`admin-upload-${key}`} />
          {current && (
            <button
              onClick={() => saveField(key, "")}
              className="font-mono-label text-[10px] uppercase tracking-widest border-2 border-[color:var(--ink)] rounded-full px-2 py-1"
            >
              Temizle ✕
            </button>
          )}
        </div>
        <input
          placeholder="veya URL yapıştır"
          defaultValue={current && current.startsWith("http") ? current : ""}
          onBlur={(e) => e.target.value && saveField(key, e.target.value)}
          className="mt-2 w-full bg-[color:var(--bone-soft)] border-2 border-[color:var(--ink)] rounded-md p-1.5 text-xs outline-none"
        />
      </div>
    );
  };

  const renderStringList = (name, label, addLabel) => (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="font-mono-label text-[10px] uppercase tracking-widest opacity-70">{label}</span>
        <AddBtn onClick={() => addItem(name, "")} label={addLabel} testid={`admin-add-${name}`} />
      </div>
      <div className="space-y-2">
        {(lists[name] || []).map((v, i) => (
          <div key={i} className="flex gap-2 items-center">
            <TextInput value={v} onChange={(val) => patchItem(name, i, val)} testid={`admin-${name}-${i}`} />
            <RemoveBtn onClick={() => removeItem(name, i)} testid={`admin-remove-${name}-${i}`} />
          </div>
        ))}
      </div>
    </div>
  );

  const activeDef = SECTIONS.find((s) => s.id === activeSection) || SECTIONS[0];

  const renderSection = () => {
    switch (activeSection) {
      case "hero":
        return (
          <div className="space-y-4 max-w-2xl">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field defaultValue={site.get("hero.name")} label="İsim" onSave={(v) => saveField("hero.name", v)} testid="admin-field-hero.name" />
              <Field defaultValue={site.get("hero.tag")} label="Etiket (örn: işler & sözler)" onSave={(v) => saveField("hero.tag", v)} testid="admin-field-hero.tag" />
            </div>
            <Field defaultValue={site.get("hero.blurb")} label="Alt yazı" textarea onSave={(v) => saveField("hero.blurb", v)} testid="admin-field-hero.blurb" />
          </div>
        );
      case "manifesto":
        return (
          <div className="space-y-6 max-w-2xl">
            <Field defaultValue={site.get("manifesto.title")} label="Başlık" onSave={(v) => saveField("manifesto.title", v)} testid="admin-field-manifesto.title" />
            <Field defaultValue={site.get("manifesto.intro")} label="Giriş" textarea onSave={(v) => saveField("manifesto.intro", v)} testid="admin-field-manifesto.intro" />
            <div className="flex items-center justify-between">
              <span className="font-mono-label text-[10px] uppercase tracking-widest opacity-70">Maddeler</span>
              <AddBtn onClick={() => addItem("manifesto", { title: "Yeni madde", body: "Açıklama…" })} label="Madde" testid="admin-add-manifesto" />
            </div>
            {(lists.manifesto || []).map((it, i) => (
              <Card key={i} testid={`admin-manifesto-${i}`}>
                <TextInput value={it.title} onChange={(v) => patchItem("manifesto", i, { title: v })} placeholder="Başlık" testid={`admin-manifesto-title-${i}`} />
                <TextInput value={it.body} onChange={(v) => patchItem("manifesto", i, { body: v })} placeholder="Açıklama" textarea testid={`admin-manifesto-body-${i}`} />
                <RemoveBtn onClick={() => removeItem("manifesto", i)} testid={`admin-remove-manifesto-${i}`} />
              </Card>
            ))}
          </div>
        );
      case "works":
        return (
          <div className="space-y-6 max-w-2xl">
            <Field defaultValue={site.get("works.title")} label="Başlık" onSave={(v) => saveField("works.title", v)} testid="admin-field-works.title" />
            <div className="flex items-center justify-between">
              <span className="font-mono-label text-[10px] uppercase tracking-widest opacity-70">Deneyimler</span>
              <AddBtn onClick={() => addItem("works", { role: "Yeni pozisyon", org: "Kurum", period: "", location: "", accent: "#ff87ab", bullets: [], tags: [] })} label="Deneyim" testid="admin-add-works" />
            </div>
            {(lists.works || []).map((it, i) => (
              <Card key={i} testid={`admin-works-${i}`}>
                <TextInput value={it.role} onChange={(v) => patchItem("works", i, { role: v })} placeholder="Pozisyon" testid={`admin-works-role-${i}`} />
                <TextInput value={it.org} onChange={(v) => patchItem("works", i, { org: v })} placeholder="Kurum" />
                <div className="grid grid-cols-2 gap-2">
                  <TextInput value={it.period} onChange={(v) => patchItem("works", i, { period: v })} placeholder="Tarih" />
                  <TextInput value={it.location} onChange={(v) => patchItem("works", i, { location: v })} placeholder="Konum" />
                </div>
                <ColorInput value={it.accent} onChange={(v) => patchItem("works", i, { accent: v })} />
                <LinesInput value={it.bullets} onChange={(v) => patchItem("works", i, { bullets: v })} label="Maddeler" testid={`admin-works-bullets-${i}`} />
                <LinesInput value={it.tags} onChange={(v) => patchItem("works", i, { tags: v })} label="Etiketler" />
                <RemoveBtn onClick={() => removeItem("works", i)} testid={`admin-remove-works-${i}`} />
              </Card>
            ))}
          </div>
        );
      case "projects":
        return (
          <div className="space-y-6 max-w-2xl">
            <Field defaultValue={site.get("projects.title")} label="Başlık" onSave={(v) => saveField("projects.title", v)} testid="admin-field-projects.title" />
            <Field defaultValue={site.get("projects.blurb")} label="Alt yazı" textarea onSave={(v) => saveField("projects.blurb", v)} testid="admin-field-projects.blurb" />
            <div className="flex items-center justify-between">
              <span className="font-mono-label text-[10px] uppercase tracking-widest opacity-70">Projeler (foto + açıklama)</span>
              <AddBtn onClick={() => addItem("projects", { title: "Yeni proje", blurb: "Açıklama…", photo: "" })} label="Proje" testid="admin-add-projects" />
            </div>
            {(lists.projects || []).map((p, i) => (
              <Card key={i} testid={`admin-projects-${i}`}>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                  <div className="md:col-span-4">
                    {p.photo ? (
                      <img src={p.photo} alt="" className="w-full h-28 object-cover rounded border border-[color:var(--ink)]" />
                    ) : (
                      <div className="w-full h-28 flex items-center justify-center rounded border border-dashed border-[color:var(--ink)] opacity-50 text-xs">Fotoğraf yok</div>
                    )}
                    <input type="file" accept="image/*" onChange={uploadProjectPhoto(i)} className="mt-2 text-xs" data-testid={`admin-project-photo-${i}`} />
                    <input
                      placeholder="veya foto URL"
                      defaultValue={p.photo && p.photo.startsWith("http") ? p.photo : ""}
                      onBlur={(e) => e.target.value && patchItem("projects", i, { photo: e.target.value })}
                      className="mt-2 w-full bg-[color:var(--bone-soft)] border-2 border-[color:var(--ink)] rounded-md p-1.5 text-xs outline-none"
                    />
                  </div>
                  <div className="md:col-span-8 space-y-2">
                    <TextInput value={p.title} onChange={(v) => patchItem("projects", i, { title: v })} placeholder="Başlık" testid={`admin-project-title-${i}`} />
                    <TextInput value={p.blurb} onChange={(v) => patchItem("projects", i, { blurb: v })} placeholder="Açıklama" textarea testid={`admin-project-blurb-${i}`} />
                    <RemoveBtn onClick={() => removeItem("projects", i)} testid={`admin-remove-projects-${i}`} />
                  </div>
                </div>
              </Card>
            ))}
          </div>
        );
      case "skills":
        return (
          <div className="space-y-6 max-w-2xl">
            <Field defaultValue={site.get("skills.title")} label="Başlık" onSave={(v) => saveField("skills.title", v)} testid="admin-field-skills.title" />
            <div className="flex items-center justify-between">
              <span className="font-mono-label text-[10px] uppercase tracking-widest opacity-70">Yetenek grupları</span>
              <AddBtn onClick={() => addItem("skills", { label: "Yeni grup", color: "#ff5d8f", items: [] })} label="Grup" testid="admin-add-skills" />
            </div>
            {(lists.skills || []).map((g, i) => (
              <Card key={i} testid={`admin-skills-${i}`}>
                <TextInput value={g.label} onChange={(v) => patchItem("skills", i, { label: v })} placeholder="Grup adı" testid={`admin-skills-label-${i}`} />
                <ColorInput value={g.color} onChange={(v) => patchItem("skills", i, { color: v })} />
                <LinesInput value={g.items} onChange={(v) => patchItem("skills", i, { items: v })} label="Yetenekler" testid={`admin-skills-items-${i}`} />
                <RemoveBtn onClick={() => removeItem("skills", i)} testid={`admin-remove-skills-${i}`} />
              </Card>
            ))}
          </div>
        );
      case "langedu":
        return (
          <div className="space-y-8 max-w-2xl">
            <div className="space-y-4">
              <Field defaultValue={site.get("languages.title")} label="Diller başlığı" onSave={(v) => saveField("languages.title", v)} testid="admin-field-languages.title" />
              <div className="flex items-center justify-between">
                <span className="font-mono-label text-[10px] uppercase tracking-widest opacity-70">Diller</span>
                <AddBtn onClick={() => addItem("languages", { name: "Dil", level: "Seviye" })} label="Dil" testid="admin-add-languages" />
              </div>
              {(lists.languages || []).map((l, i) => (
                <Card key={i} testid={`admin-languages-${i}`}>
                  <div className="grid grid-cols-2 gap-2">
                    <TextInput value={l.name} onChange={(v) => patchItem("languages", i, { name: v })} placeholder="Dil" testid={`admin-languages-name-${i}`} />
                    <TextInput value={l.level} onChange={(v) => patchItem("languages", i, { level: v })} placeholder="Seviye" />
                  </div>
                  <RemoveBtn onClick={() => removeItem("languages", i)} testid={`admin-remove-languages-${i}`} />
                </Card>
              ))}
            </div>
            <div className="space-y-4">
              <Field defaultValue={site.get("education.title")} label="Eğitim başlığı" onSave={(v) => saveField("education.title", v)} testid="admin-field-education.title" />
              <div className="flex items-center justify-between">
                <span className="font-mono-label text-[10px] uppercase tracking-widest opacity-70">Eğitim</span>
                <AddBtn onClick={() => addItem("education", { school: "Okul", period: "", degree: "Derece", note: "", tags: [] })} label="Eğitim" testid="admin-add-education" />
              </div>
              {(lists.education || []).map((ed, i) => (
                <Card key={i} testid={`admin-education-${i}`}>
                  <div className="grid grid-cols-2 gap-2">
                    <TextInput value={ed.school} onChange={(v) => patchItem("education", i, { school: v })} placeholder="Okul" testid={`admin-education-school-${i}`} />
                    <TextInput value={ed.period} onChange={(v) => patchItem("education", i, { period: v })} placeholder="Tarih" />
                  </div>
                  <TextInput value={ed.degree} onChange={(v) => patchItem("education", i, { degree: v })} placeholder="Derece" />
                  <TextInput value={ed.note} onChange={(v) => patchItem("education", i, { note: v })} placeholder="Not" textarea />
                  <LinesInput value={ed.tags} onChange={(v) => patchItem("education", i, { tags: v })} label="Etiketler" />
                  <RemoveBtn onClick={() => removeItem("education", i)} testid={`admin-remove-education-${i}`} />
                </Card>
              ))}
            </div>
          </div>
        );
      case "contact":
        return (
          <div className="space-y-4 max-w-2xl">
            <Field defaultValue={site.get("contact.title")} label="Başlık" onSave={(v) => saveField("contact.title", v)} testid="admin-field-contact.title" />
            <Field defaultValue={site.get("contact.blurb")} label="Alt yazı" textarea onSave={(v) => saveField("contact.blurb", v)} testid="admin-field-contact.blurb" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field defaultValue={site.get("contact.email")} label="E-posta" onSave={(v) => saveField("contact.email", v)} testid="admin-field-contact.email" />
              <Field defaultValue={site.get("contact.phone")} label="Telefon" onSave={(v) => saveField("contact.phone", v)} testid="admin-field-contact.phone" />
            </div>
            <Field defaultValue={site.get("contact.linkedin")} label="LinkedIn linki" onSave={(v) => saveField("contact.linkedin", v)} testid="admin-field-contact.linkedin" />
          </div>
        );
      case "footer":
        return (
          <div className="space-y-6 max-w-2xl">
            <Field defaultValue={site.get("footer.text")} label="Footer metni" textarea onSave={(v) => saveField("footer.text", v)} testid="admin-field-footer.text" />
            {renderStringList("colophon", "Künye satırları", "satır")}
          </div>
        );
      case "media":
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-3xl">
            {renderMediaCard("photos.hero", "Hero medyası (foto veya video)")}
            {renderMediaCard("photos.about", "Hakkında fotoğrafı")}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[95] bg-[color:var(--paper)] flex flex-col"
          data-testid="admin-panel"
        >
          {savedFlash && (
            <div
              className="fixed top-4 right-4 z-[97] px-3 py-1.5 rounded-full border-2 border-[color:var(--ink)] bg-[color:var(--grass)] font-mono-label text-[10px] uppercase tracking-widest shadow-[3px_3px_0_0_var(--ink)]"
              data-testid="admin-saved-flash"
            >
              Kaydedildi ✓
            </div>
          )}

          <div className="shrink-0 border-b-2 border-[color:var(--ink)] px-4 md:px-8 py-4 flex items-center justify-between gap-4 bg-[color:var(--paper)]">
            <div
              className="rounded-lg border-2 border-[color:var(--ink)] px-4 py-1.5 font-serif-editorial italic text-lg md:text-2xl"
              style={{ background: "var(--pink)", color: "var(--ink)" }}
              data-testid="admin-love-header"
            >
              Doruk Seni Çok Seviyor ♡
            </div>
            <button onClick={() => setOpen(false)} className="btn-pill ghost" data-testid="admin-close">
              Kapat ✕
            </button>
          </div>

          {!loggedIn ? (
            <div className="flex-1 flex items-center justify-center p-6">
              <form onSubmit={login} className="panel p-8 w-full max-w-md space-y-4" data-testid="admin-login">
                <div className="font-mono-label text-[10px] uppercase tracking-widest opacity-70">
                  Denetim paneli · sadece Duru
                </div>
                <h2 className="display-serif italic text-4xl">Site editörü</h2>
                <label className="block">
                  <span className="font-mono-label text-[10px] uppercase tracking-widest opacity-70">Kullanıcı adı</span>
                  <input
                    value={creds.user}
                    onChange={(e) => setCreds({ ...creds, user: e.target.value })}
                    className="mt-1 w-full bg-transparent border-b-2 border-[color:var(--ink)] py-2 font-serif-editorial italic text-xl outline-none"
                    data-testid="admin-user"
                  />
                </label>
                <label className="block">
                  <span className="font-mono-label text-[10px] uppercase tracking-widest opacity-70">Şifre</span>
                  <input
                    type="password"
                    value={creds.pass}
                    onChange={(e) => setCreds({ ...creds, pass: e.target.value })}
                    className="mt-1 w-full bg-transparent border-b-2 border-[color:var(--ink)] py-2 font-serif-editorial italic text-xl outline-none"
                    data-testid="admin-pass"
                  />
                </label>
                {err && (
                  <div className="font-mono-label text-[10px] uppercase tracking-widest text-[color:var(--persimmon)]" data-testid="admin-error">
                    {err}
                  </div>
                )}
                <button type="submit" className="btn-pill warm" data-testid="admin-submit">
                  Giriş yap →
                </button>
              </form>
            </div>
          ) : (
            <div className="flex-1 flex min-h-0" data-testid="admin-editor">
              <nav
                className="shrink-0 w-48 md:w-60 border-r-2 border-[color:var(--ink)] overflow-y-auto p-3 bg-[color:var(--bone-soft)]"
                data-lenis-prevent
              >
                {SECTIONS.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => setActiveSection(s.id)}
                    data-testid={`admin-nav-${s.id}`}
                    className={`w-full text-left px-3 py-2 mb-1 rounded-md font-mono-label text-[11px] uppercase tracking-widest transition-colors ${
                      activeSection === s.id
                        ? "bg-[color:var(--ink)] text-[color:var(--paper)]"
                        : "hover:bg-[color:var(--pink)]"
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
                <div className="h-px bg-[color:var(--hair)] my-3" />
                <button
                  onClick={() => {
                    if (window.confirm("Tüm değişiklikleri sıfırlamak istiyor musun?")) {
                      site.reset();
                      hydrate();
                      flashSaved();
                    }
                  }}
                  className="w-full text-left px-3 py-2 mb-1 rounded-md font-mono-label text-[11px] uppercase tracking-widest hover:bg-[color:var(--pink)]"
                  data-testid="admin-reset"
                >
                  Fabrika ayarları
                </button>
                <button
                  onClick={() => {
                    localStorage.removeItem("duru_admin_token");
                    setLoggedIn(false);
                  }}
                  className="w-full text-left px-3 py-2 rounded-md font-mono-label text-[11px] uppercase tracking-widest hover:bg-[color:var(--pink)]"
                  data-testid="admin-logout"
                >
                  Çıkış
                </button>
              </nav>

              <div
                className="flex-1 overflow-y-auto p-6 md:p-10"
                style={{ overscrollBehavior: "contain" }}
                data-lenis-prevent
              >
                <h3 className="display-serif italic text-3xl md:text-4xl mb-6 flex items-center gap-3">
                  <span className="inline-block w-2 h-2 rounded-full bg-[color:var(--cobalt)]" />
                  {activeDef.label}
                </h3>
                {renderSection()}
                <div className="mt-10 pb-10 font-mono-label text-[10px] uppercase tracking-widest opacity-50">
                  Türkçe yaz · site seçili dile otomatik çevrilir · Esc ile kapat
                </div>
              </div>
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
